import * as React from "react";
import { ActionEvent } from "../types";
import { observer } from "mobx-react-lite";
import { TaskViewVisualStore } from "../stores";
import ActorBadge from "./ActorBadge";
import TasksRootStore from "../stores/TasksRootStore";
import { Utils } from "../../common/misc/Utils";
import { List, Popover, Space, Tooltip } from "antd";
import {
    CheckCircleTwoTone,
    CloseCircleTwoTone,
    FieldTimeOutlined,
    MessageOutlined,
    ProfileOutlined,
    SyncOutlined,
    UnorderedListOutlined,
    WarningTwoTone,
} from "@ant-design/icons";
import {
    getActionEventObjectTable,
    isActionEventObjectValue,
} from "./ActionEventObjectTable";

type Props = {
    store: TaskViewVisualStore;
    rootStore: TasksRootStore;
    event: ActionEvent;
};

const ActionEventView: React.FC<Props> = ({ store, rootStore, event }) => {
    const getStatusIcon = () => {
        switch (event.status) {
            case "Completed":
                return <CheckCircleTwoTone twoToneColor="green" />;
            case "Failed":
                return <CloseCircleTwoTone twoToneColor="red" />;
            case "AttentionRequired":
                return <WarningTwoTone twoToneColor="#ffc300" />;
            default:
                return <SyncOutlined spin />;
        }
    };

    return (
        <List.Item
            extra={
                <Space>
                    <Popover
                        title="Timings"
                        content={getActionEventObjectTable({
                            Started: Utils.formatDateStringShort(
                                event.triggeredOn,
                            ),
                            Completed: event.completedOn
                                ? Utils.formatDateStringShort(event.completedOn)
                                : undefined,
                            Duration: Utils.getDuration(
                                event.triggeredOn,
                                event.completedOn,
                            ),
                        })}
                    >
                        <FieldTimeOutlined />
                    </Popover>
                    {isActionEventObjectValue(event.fields) &&
                        Object.keys(event.fields).length > 0 && (
                            <Popover
                                title="Fields"
                                content={getActionEventObjectTable(
                                    event.fields,
                                )}
                                placement="bottom"
                            >
                                <UnorderedListOutlined />
                            </Popover>
                        )}
                    {isActionEventObjectValue(event.result) &&
                        Object.keys(event.result).length > 0 && (
                            <Popover
                                title="Result"
                                content={getActionEventObjectTable(
                                    event.result,
                                )}
                                placement="bottom"
                            >
                                <ProfileOutlined />
                            </Popover>
                        )}
                    {event.message && (
                        <Popover
                            title="Message"
                            content={event.message}
                            placement="bottom"
                        >
                            <MessageOutlined />
                        </Popover>
                    )}
                    {getStatusIcon()}
                </Space>
            }
        >
            <List.Item.Meta
                avatar={
                    <ActorBadge
                        store={store}
                        actor={event.triggeredByActor}
                        legacyUserId={event.userId}
                        legacyUserName={
                            store.allUsersFullNameResolver[event.userId]
                        }
                        className="author-avatar"
                        size={24}
                        showName={false}
                    />
                }
                title={
                    <>
                        {rootStore.getActionName(event.actionDefinitionId)}
                        {event.packageId && (
                            <Tooltip
                                title={store.getAttachmentName(event.packageId)}
                            >
                                <i className="alpha-icon attachment xxs" />
                            </Tooltip>
                        )}
                    </>
                }
                description={Utils.formatDateStringShort(event.triggeredOn)}
            />
        </List.Item>
    );
};

export default observer(ActionEventView);
